import { createContext, useContext, useState, useEffect, PropsWithChildren, use } from "react";
import { useParams } from "react-router-dom";

import { SampleConfiguration, SampleConfigurationSet, SampleSets } from './sampleSets.ts';
import { AppConfigurationContext } from './appConfigurationProvider.tsx';
import { MetadataContext, MetaDataLoadingState } from './metadataProvider.tsx';
import { BeamlineContext, BeamlineLoadingState } from './beamlineProvider.tsx';
import { EsafDto, SampleGetParametersByNameDto, getSetsForEsaf, getSamplesForEsaf, getEsafParameterValues } from './api/api.ts';


// This is a "context provider" React component for a SampleSets instance.

// It keeps its own internal instance of SampleSets and
// provides hooks for a few additional functions to manipulate it.
// It's designed to be placed once in the DOM, and it must be beneath
// three other providers: MetadataProvider, AppConfigurationProvider, and BeamlineProvider.

// This design allows relatively free access to the current state of all the
// Sets and Samples, but of course creates the usual performance problem
// of the set being monolithic.  That is, even if we just change one parameter
// in one SampleConfiguration and it's not the value being sorted on, which would
// require us to only update a single table cell in the DOM, this design
// invalidates the content of the entire table, forcing both a re-sort and a re-draw.
// If we're dealing with config sets larger than, say, 1000, with dozens of parameters,
// we might need to redesign.


// This component should be provided with an ESAF slug to use as a reference when fetching configuration sets.
// If the component is given a null value it will skip loading,
// then try again as soon as the value is changed.

enum SampleSetsLoadingState { NotTriggered, Pending, Succeeded, Failed };

// The structure we are providing to components in the hierarchy below the provider
interface SampleConfigurationInterface {
  esaf: EsafDto | undefined;
  sets: SampleSets;
  // Track the status of loading the SampleSets and ScanTypes.
  loadingState: SampleSetsLoadingState;
  // A callback for when a child component makes a change to the SampleSets instance.
  changed: () => void;
  // This counter is incremented whenever the SampleSets data is changed.
  // Components can watch this counter to know when they need to re-render,
  // or compare existing cached data to check for a re-render.
  // Actually more straightforward than providing a registration point for callbacks,
  // since we can leverage useEffect.
  changeCounter: number;
}

const defaultInterface: SampleConfigurationInterface = {
  esaf: undefined,
  sets: new SampleSets("empty", "unknown"), // Should never be reached
  loadingState: SampleSetsLoadingState.NotTriggered,
  changed: () => {},
  changeCounter: 0
}

interface ProviderProps {
  esafSlug?: string;
}

const SampleSetsContext = createContext<SampleConfigurationInterface>(defaultInterface);

const SampleSetsProvider: React.FC<PropsWithChildren<ProviderProps>> = (props) => {
  let { esafSlug: esafSlugFromParams } = useParams();
  if (!esafSlugFromParams) {
    esafSlugFromParams = props.esafSlug;
  }
  const [esafSlug, setEsafSlug] = useState<string | undefined>(esafSlugFromParams);

  const appConfig = useContext(AppConfigurationContext);
  const metaData = useContext(MetadataContext);
  const beamlineContext = useContext(BeamlineContext);
  
  const [esaf, setEsaf] = useState<EsafDto | undefined>(undefined);
  const [sampleConfigurationsObject, setSampleConfigurationsObject] = useState<SampleSets>(defaultInterface.sets);

  const [loadingState, setLoadingState] = useState<SampleSetsLoadingState>(SampleSetsLoadingState.NotTriggered);

  const [changeCounter, setChangeCounter] = useState<number>(0);


  useEffect(() => {
    appConfig.log('SampleSetsProvider mounted');

    return () => {
      appConfig.log('SampleSetsProvider unmounted');
    };
  }, []);


  useEffect(() => {
    // Proceed only if our watched is "Pending":
    if (loadingState != SampleSetsLoadingState.Pending) { return; }

    const fetchData = async () => {
      try {
        if (esafSlug === undefined) { throw new Error("ESAF Slug not defined"); }

        const p = esafSlug.trim()
        if (!p) {
          setLoadingState(SampleSetsLoadingState.Failed);
          appConfig.log("ESAF Slug is blank.");
          throw new Error("ESAF Slug is blank");
        }
        if (metaData.loadingState != MetaDataLoadingState.Succeeded) {
          appConfig.log("SampleSets provider: Metadata not loaded yet.");
          // Don't throw, just try again later.
          return;
        }
        if (beamlineContext.loadingState != BeamlineLoadingState.Succeeded) {
          appConfig.log("SampleSets provider: Beamline not loaded yet.");
          // Don't throw, just try again later.
          return;
        }

        const esaf = metaData.esafs.bySlug.get(p);
        if (!esaf) {
          setLoadingState(SampleSetsLoadingState.Failed);
          appConfig.log("No ESAF found with slug: " + p);
          throw new Error("No ESAF found with slug: " + p);
        }
        setEsaf(esaf);

        // Use Promise.all to ask for everything at once.
        const [setDtosResult, sampleDtosResult, esafParameterValuesResult] = await Promise.all([
          getSetsForEsaf(esafSlug),
          getSamplesForEsaf(esafSlug),
          getEsafParameterValues(esafSlug),
        ]);

        if (!setDtosResult.success || !sampleDtosResult.success || !esafParameterValuesResult.success) {
          const message = `Error fetching metadata: ${setDtosResult.message} ${sampleDtosResult.message} ${esafParameterValuesResult.message}`;
          appConfig.log(message);
          setLoadingState(SampleSetsLoadingState.Failed);
          return;
        }

        appConfig.log('Ingesting ScanTypes, Sets, Samples, and Parameter values.');
        // Create a master container for all our sets
        const setContainer = new SampleSets(esafSlug!, esafSlug!);
        setContainer.setScanTypes(beamlineContext.scanTypes);
        const sets = setDtosResult.response!.results.map((dto) => {
          return new SampleConfigurationSet(dto);
        });
        setContainer.add(sets);

        // Turn these into a lookup table by slug to we can pair them with sample Dtos
        const sampleValuesBySampleSlug: Map<string, SampleGetParametersByNameDto> = new Map();
        esafParameterValuesResult.response!.forEach((dto) => {
          sampleValuesBySampleSlug.set(dto.slug, dto);
        });

        sampleDtosResult.response!.results.forEach((dto) => {
          const parametersByNameDto = sampleValuesBySampleSlug.get(dto.slug);
          const c = new SampleConfiguration(dto, parametersByNameDto ? parametersByNameDto.parameter_values_by_name : {});
          const thisSet = setContainer.getBySlug(c.setSlug);
          if (!thisSet) { return; }
          thisSet.add([c]);
        });
        setSampleConfigurationsObject(setContainer);

        setLoadingState(SampleSetsLoadingState.Succeeded);
        return;
      } catch (error) {
        appConfig.log('Exception fetching SampleSetsProvider data: ' + error);
      }
      appConfig.log('Setting SampleSetsProvider loading state as failed');
      setLoadingState(SampleSetsLoadingState.Failed);
    };

    appConfig.log('SampleSetsProvider calling fetchData');
    fetchData();

  }, [metaData.loadingState, beamlineContext.loadingState, loadingState]);


  useEffect(() => {
    if (esafSlug === undefined) {
      appConfig.log('SampleSetsProvider given undefined esafSlug');
      return;
    }
    appConfig.log('SampleSetsProvider given esafSlug: ' + esafSlug);
    setLoadingState(SampleSetsLoadingState.Pending);
  }, [esafSlug]);


  // A callback for when a child component makes a change to the SampleSets instance.
  // Note: In the future it may be prudent to pass a set ID into this,
  // to trigger separate server data update calls for each set, since each will have its own history.
  function changed() {
    appConfig.log("Called changed() in SampleSetsProvider.");
    setChangeCounter(changeCounter + 1);
  };


  return (
    <SampleSetsContext.Provider value={{
        esaf: esaf,
        sets: sampleConfigurationsObject,
        loadingState: loadingState,
        changed: changed,
        changeCounter: changeCounter
    }}>
      {props.children}
    </SampleSetsContext.Provider>
  )
}

export { SampleSetsContext, SampleSetsProvider, SampleSetsLoadingState }